import { useSyncExternalStore } from 'react';
import { en, type Strings } from './en';
import { fr } from './fr';
import { ko } from './ko';

export type { Strings } from './en';
export type Lang = 'en' | 'fr' | 'ko';
export const LANGS: readonly Lang[] = ['en', 'fr', 'ko'];

const DICTIONARIES: Readonly<Record<Lang, Strings>> = { en, fr, ko };
const KEY = 'halvard.lang';

/** Each language's name in itself: "Français", "한국어". */
export const LANG_NAMES: Readonly<Record<Lang, string>> = {
  en: en.langName,
  fr: fr.langName,
  ko: ko.langName,
};

function stored(): Lang | null {
  try {
    const value = localStorage.getItem(KEY);
    return LANGS.find((lang) => lang === value) ?? null;
  } catch {
    return null;
  }
}

/** The browser's first choice among the site's languages, English when it prefers none. */
export function browserLang(languages: readonly string[]): Lang {
  for (const tag of languages) {
    const base = tag.toLowerCase().split('-')[0];
    const lang = LANGS.find((l) => l === base);
    if (lang) return lang;
  }
  return 'en';
}

let current: Lang =
  stored() ??
  browserLang(
    typeof navigator === 'undefined' ? [] : (navigator.languages ?? [navigator.language]),
  );
const listeners = new Set<() => void>();

function applyToDocument(lang: Lang): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = lang;
  document
    .querySelector('meta[name="description"]')
    ?.setAttribute('content', DICTIONARIES[lang].meta.description);
}
applyToDocument(current);

export function getLang(): Lang {
  return current;
}

/** Switches the language everywhere and remembers it on this device. */
export function setLang(lang: Lang): void {
  if (lang === current) return;
  current = lang;
  try {
    localStorage.setItem(KEY, lang);
  } catch {
    // Private windows: the choice lasts until the page closes.
  }
  applyToDocument(lang);
  for (const listener of listeners) listener();
}

/** The words of the current language, for code outside React (stores, log sentences). */
export function t(): Strings {
  return DICTIONARIES[current];
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The current language; the component renders again when it changes. */
export function useLang(): Lang {
  return useSyncExternalStore(subscribe, getLang, getLang);
}

/** The words of the current language; the component renders again when it changes. */
export function useT(): Strings {
  return DICTIONARIES[useLang()];
}
